// v10/src/components/PhotoGallery.js
import React, { useState } from 'react';
import { Camera, X, CloudOff, Cloud, Loader } from 'lucide-react';
import { useOfflineSync } from '../hooks/useOfflineSync';

const PhotoGallery = ({ photos, onAddPhoto, onRemovePhoto, itemKey, darkMode, aircraftId }) => {
  const { isOnline, savePhotoLocally, syncing } = useOfflineSync();
  const [uploading, setUploading] = useState(false);

  const handleFileInput = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;

    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const dataUrl   = event.target.result;
        const base64    = dataUrl.split(',')[1];
        const mediaType = file.type;

        // 1. Guardar en estado React (para mostrar inmediatamente)
        onAddPhoto(itemKey, dataUrl, file.name);

        // 2. Guardar en IndexedDB para sync posterior
        try {
          await savePhotoLocally({
            image:      base64,
            mediaType,
            aircraftId: aircraftId || 'unknown',
            itemKey,
            context:    { fileName: file.name },
          });
        } catch (err) {
          console.warn('[PhotoGallery] No se pudo guardar localmente:', err.message);
        }

        setUploading(false);
      };
      reader.onerror = () => setUploading(false);
      reader.readAsDataURL(file);
    } catch (err) {
      console.error('[PhotoGallery] Error:', err);
      setUploading(false);
    }
  };

  return (
    <div className="mt-3 space-y-2">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <p className={`text-xs font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          📸 Fotos de inspección:
        </p>
        {/* Indicador offline/sync */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {!isOnline ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '10px', fontWeight: 700, color: '#f59e0b' }}>
              <CloudOff style={{ width: '11px', height: '11px' }} />
              Offline — se sincronizará
            </span>
          ) : syncing ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '10px', fontWeight: 700, color: '#3b82f6' }}>
              <Loader style={{ width: '11px', height: '11px', animation: 'spin 1s linear infinite' }} />
              Sincronizando...
            </span>
          ) : (photos?.length > 0) ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '10px', fontWeight: 600, color: '#22c55e' }}>
              <Cloud style={{ width: '11px', height: '11px' }} />
              Online
            </span>
          ) : null}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {photos?.map((photo, idx) => (
          <div key={idx} className="relative group">
            <img
              src={photo.data}
              alt={photo.name}
              className="w-20 h-20 object-cover rounded-lg border-2 border-gray-300 dark:border-gray-600 hover:border-blue-500 transition-all"
              style={{ aspectRatio: '1/1' }}
            />
            <button
              onClick={() => onRemovePhoto(itemKey, idx)}
              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:bg-red-600"
              title="Eliminar foto"
            >
              <X className="w-3 h-3" />
            </button>
            <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs px-1 py-0.5 rounded-b-lg opacity-0 group-hover:opacity-100 transition-opacity truncate">
              {photo.name}
            </div>
          </div>
        ))}

        {/* Botón agregar foto */}
        <label className={`w-20 h-20 flex flex-col items-center justify-center border-2 border-dashed rounded-lg cursor-pointer transition-all ${
          uploading ? 'opacity-50 cursor-not-allowed' :
          darkMode
            ? 'border-gray-500 hover:border-blue-400 bg-gray-700/30 hover:bg-gray-600/50'
            : 'border-gray-300 hover:border-blue-500 bg-gray-50 hover:bg-blue-50'
        }`}>
          <input
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={handleFileInput}
            disabled={uploading}
          />
          {uploading ? (
            <Loader className={`w-6 h-6 ${darkMode ? 'text-blue-400' : 'text-blue-500'}`}
              style={{ animation: 'spin 1s linear infinite' }} />
          ) : (
            <Camera className={`w-6 h-6 ${darkMode ? 'text-gray-300' : 'text-gray-500'}`} />
          )}
          <span className={`text-xs mt-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {uploading ? 'Guardando...' : 'Agregar'}
          </span>
        </label>
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default PhotoGallery;
